// src/App.js
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import SubmitProblem from "./pages/SubmitProblem";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import DashboardClient from "./pages/DashboardClient";
import DashboardHelper from "./pages/DashboardHelper";
import ClientHistory from "./pages/ClientHistory";
import HelperHistory from "./pages/HelperHistory";
import EditHelperProfile from "./pages/EditHelperProfile"; 
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PrivateRoute from "./components/PrivateRoute";
import RoleRoute from "./components/RoleRoute";
import { ROLE_VALUE } from "./constants/roleLabels";
import VerifyEmail from "./pages/VerifyEmail"; 
import Profile from "./pages/Profile";
import UserProfilePage from "./pages/UserProfilePage";
import ForgotPassword from "./pages/ForgotPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LegalNotice from "./pages/LegalNotice";
import About from "./pages/About";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import HelperOnboarding from "./pages/HelperOnboarding";
import CookieBanner from "./components/CookieBanner";
import CookiePreferencesModal from "./components/CookiePreferencesModal";
import AccessReadinessChecklist from "./components/AccessReadinessChecklist";

//  Import WorkspacePage
import WorkspacePage from "./pages/WorkspacePage"; // ✅ fixed

function App() {
  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          {/* Public marketing + informational pages. These keep the shared
              Navbar/Footer chrome since a signed-out visitor can land on any
              of them. */}
          <Route path="/user/:uid" element={<UserProfilePage />} />
          <Route path="/" element={<HomePage />} />
          <Route
            path="/submit-problem"
            element={
              <AccessReadinessChecklist>
                <SubmitProblem />
              </AccessReadinessChecklist>
            }
          />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal" element={<LegalNotice />} />
          <Route path="/legal-notice" element={<LegalNotice />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
        </Route>

        {/* Authenticated, full-viewport surfaces. Deliberately outside
            <Layout /> — each of these owns its own chrome (either its own
            branded auth header, or the shared OperationsShell console header)
            so the marketing navbar/footer never stacks on top of it. */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route
          path="/onboarding"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.specialist, "helper"]}>
                <HelperOnboarding />
              </RoleRoute>
            </PrivateRoute>
          }
        />
        <Route path="/become-helper" element={<Navigate to="/onboarding" replace />} />

        {/* Client History */}
        <Route
          path="/clienthistory"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.requester, "client"]}>
                <ClientHistory />
              </RoleRoute>
            </PrivateRoute>
          }
        />

        {/* Helper History */}
        <Route
          path="/helperhistory"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.specialist, "helper"]}>
                <HelperHistory />
              </RoleRoute>
            </PrivateRoute>
          }
        />

        {/* Edit Helper Profile */}
        <Route
          path="/edit-helper-profile"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.specialist, "helper"]}>
                <EditHelperProfile />
              </RoleRoute>
            </PrivateRoute>
          }
        />

        {/* Operations consoles, for both roles.
            Outside <Layout /> for the same reason as the workspace below: the
            console owns its own chrome. Inside it, an authenticated expert was
            served the marketing navbar — complete with "Login" and "Signup"
            links — stacked on top of their own dashboard header. */}
        <Route
          path="/dashboard-client"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.requester, "client"]}>
                <DashboardClient />
              </RoleRoute>
            </PrivateRoute>
          }
        />
        <Route path="/dashboardclient" element={<Navigate to="/dashboard-client" replace />} />

        <Route
          path="/dashboard-helper"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.specialist, "helper"]}>
                <DashboardHelper />
              </RoleRoute>
            </PrivateRoute>
          }
        />
        <Route path="/dashboardhelper" element={<Navigate to="/dashboard-helper" replace />} />

        {/* Workspace route (shared by both roles).
            Deliberately outside <Layout /> — the incident console owns the full
            viewport, so the marketing navbar and footer must not be there. */}
        <Route
          path="/workspace/:problemId"
          element={
            <PrivateRoute>
              <RoleRoute allowedRoles={[ROLE_VALUE.requester, ROLE_VALUE.specialist, "client", "helper"]}>
                <WorkspacePage />
              </RoleRoute>
            </PrivateRoute>
          }
        />
      </Routes>

      {/* Toasts */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        className="!top-16 sm:!top-4"
        toastClassName="!text-sm !rounded-lg"
      />
      <CookieBanner />
      <CookiePreferencesModal />
    </>
  );
}

export default App;
